import {
  useGetSculptureEditionHistory,
  useGetSculptureEditionHistoryMerged,
  useReorderSculptureEditionProvenance,
  useUpdateSculptureEditionHistorySequence,
  type SculptureEditionHistory,
  type SculptureEditionHistoryMergedAuction,
} from "@/api/sculpture-edition-history.api";
import {
  MaskableTableSection,
  MaskableTableSectionBody,
  MaskableTableSectionHeader,
} from "@/components/form-items/MaskableTableSection";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import { AddButton } from "@/components/ui/custom-buttons";
import { DataTable } from "@/components/ui/datatable";
import { RegisterVisibilityField } from "@/context/FormFieldVisibilityContext";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateOrEditSculptureEditionHistory } from "./CreateOrEditSculptureEditionHistory";

const HISTORY_TABLE_MASK_GROUP = "sculptureEditionHistoriqueTableGroup";

/** Dialog inputs unmount when closed — keep them in the Champs drawer. */
const HISTORY_DIALOG_FIELDS = [
  {
    name: "description",
    labelKey: "sculpture.history.description",
  },
  {
    name: "villePays",
    labelKey: "sculpture.history.villePays",
  },
  {
    name: "dates",
    labelKey: "sculpture.history.dates",
  },
] as const;

type SculptureEditionHistoryTableProps = {
  sculptureEditionId?: string;
  disabled?: boolean;
  provenanceMode?: boolean;
};

type HistoryRow =
  | (SculptureEditionHistory & { kind: "history" })
  | (SculptureEditionHistoryMergedAuction & { kind: "auction" });

const isHistoryRow = (
  row: HistoryRow,
): row is SculptureEditionHistory & { kind: "history" } =>
  row.kind === "history";

export const SculptureEditionHistoryTable = ({
  sculptureEditionId,
  disabled = false,
  provenanceMode = false,
}: SculptureEditionHistoryTableProps) => {
  const { t } = useTranslation();

  // Two queries — only the one matching the current mode is enabled.
  const { data: historyOnly = [] } = useGetSculptureEditionHistory(
    provenanceMode ? undefined : sculptureEditionId,
  );
  const { data: merged } = useGetSculptureEditionHistoryMerged(
    sculptureEditionId,
    provenanceMode,
  );

  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<SculptureEditionHistory>();

  const sequenceMutator = useUpdateSculptureEditionHistorySequence();
  const provenanceReorderMutator = useReorderSculptureEditionProvenance();

  useEffect(() => {
    if (provenanceMode && merged) {
      setRows([...merged.timeline]);
      return;
    }
    setRows(
      historyOnly.map<HistoryRow>((h) => ({ ...h, kind: "history" as const })),
    );
  }, [historyOnly, merged, provenanceMode]);

  const handleUpdate = (history: SculptureEditionHistory) => {
    historyToUpdate.current = history;
    setOpenEditForm(true);
  };

  const handleRowDragged = (newData: HistoryRow[]) => {
    if (!sculptureEditionId) return;

    setRows(newData);

    if (provenanceMode) {
      provenanceReorderMutator.mutate({
        sculptureEditionId,
        timeline: newData.map((r) => ({ kind: r.kind, id: r.id })),
      });
      return;
    }

    sequenceMutator.mutate({
      newSequence: newData.filter(isHistoryRow).map((h) => h.id),
      sculptureEditionId,
    });
  };

  const datatableColumns = useMemo(
    (): ColumnDef<HistoryRow>[] => [
      {
        size: 1000,
        header: t("sculpture.history.description"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.description;
          // Auction row — compose a readable label from the auction metadata.
          const heading = [
            r.auction?.ventesDescriptionCommissaires,
            r.auction?.ventesDescriptionLieu,
          ]
            .filter(Boolean)
            .join(", ");
          const parts = [
            heading,
            r.sculptureEditionDansVentesNumeroLot
              ? `Lot n°${r.sculptureEditionDansVentesNumeroLot}`
              : null,
          ].filter(Boolean);
          return parts.length
            ? parts.join(" - ")
            : t("sculpture.history.auction");
        },
      },
      {
        header: t("sculpture.history.villePays"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.villePays;
          const parts = [
            r.auction?.ventesDescriptionLieu,
            r.auction?.ventesDescriptionVille,
            r.auction?.ventesDescriptionPays,
          ].filter(Boolean);
          return parts.join(", ");
        },
      },
      {
        header: t("sculpture.history.dates"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) return r.dates;
          return r.auction?.ventesDescriptionDate ?? "";
        },
      },
      {
        header: t("sculpture.history.collectionParticuliere"),
        cell: ({ row }) => {
          const r = row.original;
          if (!isHistoryRow(r)) return null;
          return <BooleanBadge value={r.collectionParticuliere} />;
        },
      },
    ],
    [t],
  );

  const titleLabel = t(
    provenanceMode
      ? "sculpture.history.tableLabelProvenance"
      : "sculpture.history.tableLabel",
  );

  const addButton = (
    <AddButton
      type="button"
      onClick={() => setOpenCreateForm(true)}
      disabled={disabled}
    />
  );

  if (!sculptureEditionId) {
    return null;
  }

  return (
    <>
      {HISTORY_DIALOG_FIELDS.map(({ name, labelKey }) => (
        <RegisterVisibilityField
          key={name}
          name={name}
          label={t(labelKey)}
        />
      ))}
      <MaskableTableSection>
        <CreateOrEditSculptureEditionHistory
          sculptureEditionId={sculptureEditionId}
          history={historyToUpdate.current}
          open={openEditForm}
          onOpenChange={setOpenEditForm}
          disabled={disabled}
        />
        <CreateOrEditSculptureEditionHistory
          sculptureEditionId={sculptureEditionId}
          open={openCreateForm}
          onOpenChange={setOpenCreateForm}
          disabled={disabled}
        />
        <MaskableTableSectionHeader
          groupName={HISTORY_TABLE_MASK_GROUP}
          label={titleLabel}
          trailing={addButton}
        />
        <MaskableTableSectionBody groupName={HISTORY_TABLE_MASK_GROUP}>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            columns={datatableColumns}
            data={rows}
            onRowClick={(e) => {
              // Auctions are read-only: ignore clicks on them.
              if (isHistoryRow(e.original)) handleUpdate(e.original);
            }}
          />
        </MaskableTableSectionBody>
      </MaskableTableSection>
    </>
  );
};

export default SculptureEditionHistoryTable;
